import { useEffect, useMemo, useRef, useState } from "react";
import { useGlobal } from "../contexts/globalContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiSliders, FiDollarSign, FiLayers, FiGlobe, FiCheck, FiChevronDown } from "react-icons/fi";

function SearchHelper({ isCreator, compact = false, drawer = false }) {
  const { tag, sort: urlSort, min: urlMin, max: urlMax } = useParams();

  const {
    keyword,
    sort,
    setSort,
    setMin,
    setMax,
    priceRange,
    setPriceRange,
  } = useGlobal();

  useEffect(() => {
    if (urlSort !== undefined) setSort(urlSort);
    if (urlMin !== undefined) setMin(urlMin);
    if (urlMax !== undefined) setMax(urlMax);

    if (urlMin === undefined && urlMax === "5") {
      setPriceRange("under5");
    } else if (urlMin === "5" && urlMax === "10") {
      setPriceRange("5to10");
    } else if (urlMin === "10" && urlMax === "20") {
      setPriceRange("10to20");
    } else if (urlMin === "20.01" && urlMax === undefined) {
      setPriceRange("above20");
    } else {
      setPriceRange("");
    }
  }, [urlSort, urlMin, urlMax, setSort, setMin, setMax, setPriceRange]);

  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState("");
  const containerRef = useRef(null);
  const activeSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const activeItemType = activeSearchParams.get("itemType") || "all";
  const activeLanguage = activeSearchParams.get("language") || "all";

  const updateRoute = (updatedSort, updatedPriceRange, updatedItemType = activeItemType, updatedLanguage = activeLanguage) => {
    let route = "";

    if (tag) {
      route = `/user/creator/${tag}/search`;
    } else if (typeof isCreator !== "undefined" && isCreator) {
      route = "/profile/creator/search";
    } else {
      route = "/search";
    }

    if (keyword.trim()) route += `/${keyword}`;
    if (updatedSort) route += `/sort/${updatedSort}`;

    let computedMin = urlMin || "";
    let computedMax = urlMax || "";

    switch (updatedPriceRange) {
      case "under5":
        computedMin = "";
        computedMax = "5";
        break;
      case "5to10":
        computedMin = "5";
        computedMax = "10";
        break;
      case "10to20":
        computedMin = "10";
        computedMax = "20";
        break;
      case "above20":
        computedMin = "20.01";
        computedMax = "";
        break;
      default:
        computedMin = "";
        computedMax = "";
    }

    if (computedMin) route += `/min/${computedMin}`;
    if (computedMax) route += `/max/${computedMax}`;
    const query = new URLSearchParams();
    if (updatedItemType && updatedItemType !== "all") query.set("itemType", updatedItemType);
    if (updatedLanguage && updatedLanguage !== "all") query.set("language", updatedLanguage);
    const queryString = query.toString();

    navigate(queryString ? `${route}?${queryString}` : route);
  };

  const handleSortChange = (e) => {
    const newSort = e.target.value;
    setSort(newSort);
    updateRoute(newSort, priceRange, activeItemType, activeLanguage);
  };

  const handlePriceRange = (e) => {
    const newPriceRange = e.target.value;
    setPriceRange(newPriceRange);
    updateRoute(sort, newPriceRange, activeItemType, activeLanguage);
  };

  const handleItemTypeChange = (value) => {
    updateRoute(sort, priceRange, value, activeLanguage);
  };

  const handleLanguageChange = (value) => {
    updateRoute(sort, priceRange, activeItemType, value);
  };

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpenMenu("");
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const sortOptions = useMemo(
    () => [
      { value: "newest", label: "Newest" },
      { value: "recently-updated", label: "Recently Updated" },
      { value: "most-purchased", label: "Most Purchased" },
      { value: "relevance", label: "Relevance" },
      { value: "oldest", label: "Oldest" },
      { value: "price-asc", label: "Price: Low to High" },
      { value: "price-desc", label: "Price: High to Low" },
      { value: "rating-desc", label: "Rating: High to Low" },
      { value: "rating-asc", label: "Rating: Low to High" },
    ],
    []
  );

  const priceOptions = useMemo(
    () => [
      { value: "", label: "All Prices" },
      { value: "under5", label: "Under $5" },
      { value: "5to10", label: "$5 - $10" },
      { value: "10to20", label: "$10 - $20" },
      { value: "above20", label: "Above $20" },
    ],
    []
  );

  const itemTypeOptions = useMemo(
    () => [
      { value: "all", label: "All Items" },
      { value: "paid", label: "Paid" },
      { value: "free", label: "Free" },
    ],
    []
  );

  const languageOptions = useMemo(
    () => [
      { value: "all", label: "All Languages" },
      { value: "English", label: "English (all variants)" },
      { value: "English (US)", label: "American English" },
      { value: "English (UK)", label: "British English" },
      { value: "French", label: "French" },
      { value: "Spanish", label: "Spanish" },
      { value: "Arabic", label: "Arabic" },
      { value: "Portuguese", label: "Portuguese" },
      { value: "German", label: "German" },
      { value: "Italian", label: "Italian" },
    ],
    []
  );

  const selectedSortLabel =
    sortOptions.find((option) => option.value === (sort || "newest"))?.label || "Newest";
  const selectedPriceLabel =
    priceOptions.find((option) => option.value === (priceRange || ""))?.label || "All Prices";
  const selectedItemTypeLabel =
    itemTypeOptions.find((option) => option.value === activeItemType)?.label || "All Items";
  const selectedLanguageLabel =
    languageOptions.find((option) => option.value === activeLanguage)?.label || "All Languages";

  const onSortSelect = (value) => {
    handleSortChange({ target: { value } });
    setOpenMenu("");
  };

  const onPriceSelect = (value) => {
    handlePriceRange({ target: { value } });
    setOpenMenu("");
  };

  const pillClass = compact
    ? "group relative flex h-10 items-center rounded-xl border border-slate-200 bg-white px-3 shadow-sm transition hover:border-sky-200 hover:bg-sky-50"
    : "group relative flex h-12 items-center rounded-2xl border border-slate-200 bg-white px-4 shadow-sm transition hover:border-sky-200 hover:bg-sky-50";
  const optionClass =
    "w-full rounded-xl px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-100";
  const compactWidthClass = isCreator
    ? "md:w-[23%] md:min-w-[160px] lg:min-w-[170px]"
    : "md:w-[24%] md:min-w-[180px]";

  if (drawer) {
    const groups = [
      { id: 'sort', label: 'Sort by', Icon: FiSliders, value: sort || 'newest', options: sortOptions, select: onSortSelect },
      { id: 'price', label: 'Price range', Icon: FiDollarSign, value: priceRange || '', options: priceOptions, select: onPriceSelect },
      { id: 'itemType', label: 'Resource type', Icon: FiLayers, value: activeItemType, options: itemTypeOptions, select: value => { handleItemTypeChange(value); setOpenMenu(''); } },
      { id: 'language', label: 'Language', Icon: FiGlobe, value: activeLanguage, options: languageOptions, select: value => { handleLanguageChange(value); setOpenMenu(''); } },
    ];
    return <section ref={containerRef} className="category-browser space-y-3" aria-label="Search filters">
      {groups.map(({ id, label, Icon, value, options, select }) => <div key={id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <button type="button" className="flex min-h-[76px] w-full items-center gap-3 p-3 text-left transition hover:bg-sky-50"
          aria-expanded={openMenu === id} aria-controls={`filter-options-${id}`} onClick={() => setOpenMenu(openMenu === id ? '' : id)}>
          <span className="category-icon"><Icon aria-hidden="true" /></span>
          <span className="min-w-0 flex-1"><span className="block text-xs font-medium text-slate-500">{label}</span><span className="mt-1 block text-sm font-semibold text-slate-800">{options.find(option => option.value === value)?.label}</span></span>
          <FiChevronDown aria-hidden="true" className={`shrink-0 text-sky-600 transition-transform motion-reduce:transition-none ${openMenu === id ? 'rotate-180' : ''}`} />
        </button>
        {openMenu === id && <div id={`filter-options-${id}`} className="category-level border-t border-slate-100 p-2" role="group" aria-label={label}>
          {options.map(option => <button key={option.value} type="button" aria-pressed={value === option.value} onClick={() => select(option.value)}
            className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm transition ${value === option.value ? 'bg-sky-50 font-semibold text-sky-800' : 'text-slate-600 hover:bg-slate-50'}`}>
            <span>{option.label}</span>{value === option.value && <FiCheck aria-hidden="true" className="shrink-0 text-sky-600" />}
          </button>)}
        </div>}
      </div>)}
    </section>;
  }

  return (
    <section className="rounded-2xl bg-white" ref={containerRef}>
      <div className={`flex flex-wrap items-center ${compact ? "gap-2.5" : "gap-3"}`}>
        <div className={`relative w-full ${compact ? compactWidthClass : "md:w-[25%] md:min-w-[220px]"}`}>
          <button
            type="button"
            className={`${pillClass} w-full justify-between ${compact ? "text-sm" : "text-base"} font-semibold text-slate-900`}
            onClick={() => setOpenMenu((prev) => (prev === "sort" ? "" : "sort"))}
            aria-haspopup="listbox"
            aria-expanded={openMenu === "sort"}
          >
            <span>{selectedSortLabel}</span>
            <FiChevronDown
              className={`text-xl text-slate-500 transition-transform ${
                openMenu === "sort" ? "rotate-180" : ""
              }`}
            />
          </button>
          {openMenu === "sort" && (
            <div className="absolute z-20 mt-2 w-full rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSortSelect(option.value)}
                  className={`${optionClass} ${
                    option.value === (sort || "newest")
                      ? "bg-sky-50 text-sky-700"
                      : ""
                  }`}
                  role="option"
                  aria-selected={option.value === (sort || "newest")}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`relative w-full ${compact ? compactWidthClass : "md:w-[25%] md:min-w-[220px]"}`}>
          <button
            type="button"
            className={`${pillClass} w-full justify-between ${compact ? "text-sm" : "text-base"} font-semibold text-slate-900`}
            onClick={() => setOpenMenu((prev) => (prev === "price" ? "" : "price"))}
            aria-haspopup="listbox"
            aria-expanded={openMenu === "price"}
          >
            <span>{selectedPriceLabel}</span>
            <FiChevronDown
              className={`text-xl text-slate-500 transition-transform ${
                openMenu === "price" ? "rotate-180" : ""
              }`}
            />
          </button>
          {openMenu === "price" && (
            <div className="absolute z-20 mt-2 w-full rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              {priceOptions.map((option) => (
                <button
                  key={`${option.value || "all-prices"}`}
                  type="button"
                  onClick={() => onPriceSelect(option.value)}
                  className={`${optionClass} ${
                    option.value === (priceRange || "")
                      ? "bg-sky-50 text-sky-700"
                      : ""
                  }`}
                  role="option"
                  aria-selected={option.value === (priceRange || "")}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`relative w-full ${compact ? compactWidthClass : "md:w-[25%] md:min-w-[220px]"}`}>
          <button
            type="button"
            className={`${pillClass} w-full justify-between ${compact ? "text-sm" : "text-base"} font-semibold text-slate-900`}
            onClick={() => setOpenMenu((prev) => (prev === "itemType" ? "" : "itemType"))}
            aria-haspopup="listbox"
            aria-expanded={openMenu === "itemType"}
          >
            <span>{selectedItemTypeLabel}</span>
            <FiChevronDown
              className={`text-xl text-slate-500 transition-transform ${
                openMenu === "itemType" ? "rotate-180" : ""
              }`}
            />
          </button>
          {openMenu === "itemType" && (
            <div className="absolute z-20 mt-2 w-full rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              {itemTypeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    handleItemTypeChange(option.value);
                    setOpenMenu("");
                  }}
                  className={`${optionClass} ${
                    option.value === activeItemType ? "bg-sky-50 text-sky-700" : ""
                  }`}
                  role="option"
                  aria-selected={option.value === activeItemType}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`relative w-full ${compact ? compactWidthClass : "md:w-[25%] md:min-w-[220px]"}`}>
          <button
            type="button"
            className={`${pillClass} w-full justify-between ${compact ? "text-sm" : "text-base"} font-semibold text-slate-900`}
            onClick={() => setOpenMenu((prev) => (prev === "language" ? "" : "language"))}
            aria-haspopup="listbox"
            aria-expanded={openMenu === "language"}
          >
            <span>{selectedLanguageLabel}</span>
            <FiChevronDown
              className={`text-xl text-slate-500 transition-transform ${
                openMenu === "language" ? "rotate-180" : ""
              }`}
            />
          </button>
          {openMenu === "language" && (
            <div className="absolute z-20 mt-2 w-full rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              {languageOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    handleLanguageChange(option.value);
                    setOpenMenu("");
                  }}
                  className={`${optionClass} ${
                    option.value === activeLanguage ? "bg-sky-50 text-sky-700" : ""
                  }`}
                  role="option"
                  aria-selected={option.value === activeLanguage}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default SearchHelper;
